import '../styles/general.scss';
import '../styles/formField.scss';
import './textarea.scss';
import React, { useEffect, useState, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { detectIsIosDevice, maxTextareaLength } from '../Constants';
import { Spinner, useTheme } from '../../index';
const isIOS = detectIsIosDevice();
const Textarea = _ref => {
  let {
    name,
    dataName,
    label,
    floatingLabel,
    value,
    focus,
    focusOnMount,
    disabled,
    readonly,
    loading,
    color,
    className,
    error,
    disabledError,
    rows,
    onFocus,
    onChange,
    onBlur,
    onKeyDown,
    onTabClick,
    autoHeight,
    type,
    isSetDisabled,
    mask,
    preventPaste,
    minRows,
    maxRows,
    maxLength = maxTextareaLength
  } = _ref;
  const {
    theme
  } = useTheme();
  const inputRef = useRef(null);
  const [internalValue, setInternalValue] = useState(value || '');
  let activeState = false;
  useEffect(() => {
    return () => {
      isIOS && activeState && window.dispatchEvent(new Event('keyboardDidHide'));
    };
  }, []);
  useEffect(() => {
    setInternalValue(value || '');
  }, [value]);
  useEffect(() => {
    if ((focusOnMount || focus) && inputRef.current) inputRef.current.focus();
  });
  useEffect(() => {
    if (autoHeight) handleTextareaResize();
  }, [internalValue]);
  useEffect(() => {
    if (isSetDisabled) handleAutoBlur();else if (inputRef.current && !mask) {
      inputRef.current.removeAttribute('disabled');
      if (focus) inputRef.current.focus();
    }
  }, [isSetDisabled]);
  useEffect(() => {
    const textarea = inputRef.current;
    if (minRows && textarea) {
      const computedStyle = window.getComputedStyle(textarea);
      const minHeight = (parseInt(computedStyle.lineHeight, 10) || 20) * minRows;
      textarea.style.minHeight = `${minHeight}px`;
    }
  }, [minRows, inputRef.current]);
  const handleTextareaResize = () => {
    const textarea = inputRef.current;
    if (inputRef.current) {
      const computedStyle = window.getComputedStyle(textarea);
      textarea.style.height = 'auto';
      if (maxRows) {
        const maxHeight = (parseInt(computedStyle.lineHeight, 10) || 20) * maxRows;
        textarea.style.height = `${textarea.scrollHeight}px`;
        if (textarea.scrollHeight > maxHeight) {
          textarea.style.height = `${maxHeight}px`;
          textarea.style.overflowY = 'auto';
        } else {
          textarea.style.overflowY = 'hidden';
        }
        return;
      }
      textarea.style.height = `${inputRef.current.scrollHeight}px`;
    }
  };
  const handleOnchange = e => {
    setInternalValue(e.target.value);
    onChange(e.target.value);
  };
  const handleOnBlur = e => {
    activeState = false;
    isIOS && window.dispatchEvent(new Event('keyboardDidHide'));
    onBlur(e);
  };
  const handleOnFocus = e => {
    activeState = true;
    isIOS && window.dispatchEvent(new Event('keyboardDidShow'));
    onFocus(e);
  };
  const handleAutoBlur = () => {
    if (inputRef.current) {
      inputRef.current.blur();
      inputRef.current.setAttribute('disabled', true);
    }
  };
  const handleOnKeyDown = e => {
    if (e.repeat) return;
    if (!onKeyDown && !onTabClick) return;
    if (e && e.shiftKey && e.key === 'Enter') return;
    if (e.key === 'Enter') {
      e.preventDefault();
      if (onKeyDown && typeof onKeyDown === 'function') onKeyDown(e);
    }
    if (e.key === 'Tab' && onTabClick) onTabClick(e);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, type === 'default' && /*#__PURE__*/React.createElement("div", {
    className: classNames('wrts-form-field', className, theme)
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames('wrts-input-field wrts-textarea-field notranslate', theme, color, {
      error,
      'hide-placeholder': floatingLabel,
      'hide-label': !floatingLabel,
      'auto-height': autoHeight,
      'no-min-height': !!minRows
    }),
    onPointerDown: e => {
      if (inputRef.current && e.target === e.currentTarget) {
        e.preventDefault();
        inputRef.current.focus();
      }
    }
  }, /*#__PURE__*/React.createElement("textarea", {
    ref: inputRef,
    className: classNames('wrts-input notranslate', theme, color, {
      'hide-label': !floatingLabel
    }),
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    onChange: handleOnchange,
    onKeyDown: handleOnKeyDown,
    onPaste: e => {
      if (preventPaste) e.preventDefault();
    },
    placeholder: floatingLabel ? '*' : label,
    disabled: disabled,
    "data-name": dataName || name,
    rows: rows,
    style: {
      height: 'auto'
    },
    value: internalValue,
    readOnly: readonly,
    maxLength: maxLength
  }), floatingLabel && /*#__PURE__*/React.createElement("label", null, label), loading && /*#__PURE__*/React.createElement(Spinner, null)), !disabledError && /*#__PURE__*/React.createElement("span", {
    className: "error-msg",
    "data-name": name
  }, error)), type === 'simple' && /*#__PURE__*/React.createElement("textarea", {
    ref: inputRef,
    className: classNames('wrts-simple-textarea notranslate', theme, {
      'auto-height': autoHeight
    }),
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    onChange: handleOnchange,
    onKeyDown: handleOnKeyDown,
    onPaste: e => {
      if (preventPaste) e.preventDefault();
    },
    placeholder: label,
    disabled: disabled,
    "data-name": dataName || name,
    rows: rows,
    style: {
      height: 'auto'
    },
    value: internalValue,
    readOnly: readonly,
    maxLength: maxLength
  }));
};
Textarea.defaultProps = {
  type: 'default',
  className: '',
  disabled: false,
  name: '',
  dataName: '',
  rows: '1',
  floatingLabel: false,
  readonly: false,
  disabledError: false,
  onFocus: () => {},
  onChange: () => {},
  onBlur: () => {}
};
Textarea.propTypes = {
  name: PropTypes.string,
  dataName: PropTypes.string,
  label: PropTypes.string,
  floatingLabel: PropTypes.bool,
  value: PropTypes.string,
  focus: PropTypes.bool,
  focusOnMount: PropTypes.bool,
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  loading: PropTypes.bool,
  color: PropTypes.oneOf(['grey', 'light-grey']),
  className: PropTypes.string,
  error: PropTypes.string,
  disabledError: PropTypes.bool,
  rows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFocus: PropTypes.func,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  onTabClick: PropTypes.func,
  autoHeight: PropTypes.bool,
  type: PropTypes.oneOf(['default', 'simple']),
  maxLength: PropTypes.string,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  isSetDisabled: PropTypes.bool,
  mask: PropTypes.string,
  preventPaste: PropTypes.bool
};
export default /*#__PURE__*/memo(Textarea);